import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { doc, onSnapshot, getDoc } from "firebase/firestore";
import { Button } from "../common/Button";
import { X, CreditCard, ShieldCheck, Smartphone, Loader2 } from "lucide-react";
import { toast } from "react-hot-toast";
import { call, callableMessage } from "../../services/callables";
import { db, auth } from "../../lib/firebase";
import { DirectUpiPanel, DirectUpiSettings, directUpiFromSettings } from "./DirectUpiPanel";
import {
  detectPayPlatform,
  preferredPayFlow,
  parseUpiIntentMode,
  showDirectUpiOption,
  UpiIntentMode,
  isSafeUpiLink,
  payuReturnPath,
  IOS_UPI_APPS,
  PayFlow,
  UpiIntentLinks,
  savePendingUpi,
  clearPendingUpi,
  upiModalWatching,
} from "../../services/upiIntent";

interface Props {
  onClose: () => void;
  onSuccess?: () => void;
  /** Prefill amount (e.g. wallet shortfall) */
  initialAmount?: number;
  /** When true the amount can't be edited (checkout shortfall payments). */
  lockedAmount?: boolean;
  /** wallet_recharge (default) or order_payment */
  purpose?: "wallet_recharge" | "order_payment";
  /** Where PayU sends the user back to. Defaults: order_payment → checkout, else profile. */
  returnTo?: "checkout" | "profile" | "wallet";
  /** Checkout idempotency key (sent as udf4, reused as the order idempotency key on return). */
  checkoutKey?: string;
  /** Called right before the browser leaves for PayU (e.g. to persist checkout state). */
  onBeforeRedirect?: (txnid: string, amount: number) => void;
  title?: string;
}

const PRESET_AMOUNTS = [100, 500, 1000, 2000];
/** Stop polling PayU from this screen after this long (payuReconcile keeps checking server-side). */
const UPI_POLL_WINDOW_MS = 15 * 60 * 1000;
const UPI_POLL_EVERY_MS = 5000;

interface UpiState {
  txnid: string;
  amount: string;
  links: UpiIntentLinks;
  status: "waiting" | "failed" | "timeout";
  startedAt: number;
}
/** Every field the server returns must be posted — the hash covers udf1..udf5. */
const POST_FIELDS = [
  "key", "txnid", "amount", "productinfo", "firstname", "email", "phone",
  "surl", "furl", "hash", "udf1", "udf2", "udf3", "udf4", "udf5",
  "pg", "bankcode", // hosted_upi: land on PayU's UPI-app grid (empty / omitted otherwise)
];

export const AddMoneyModal: React.FC<Props> = ({
  onClose,
  initialAmount,
  lockedAmount = false,
  purpose = "wallet_recharge",
  returnTo,
  checkoutKey,
  onBeforeRedirect,
  title,
}) => {
  const [amount, setAmount] = useState(
    initialAmount && initialAmount > 0 ? String(Math.max(1, Math.ceil(initialAmount * 100) / 100)) : ""
  );
  const [loading, setLoading] = useState(false);
  const [needPhone, setNeedPhone] = useState(false);
  const [needEmail, setNeedEmail] = useState(false);
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");

  const formRef = useRef<HTMLFormElement>(null);
  const [payUParams, setPayUParams] = useState<any>(null);
  const navigate = useNavigate();
  const platform = useRef(detectPayPlatform()).current;
  const [upi, setUpi] = useState<UpiState | null>(null);
  // Direct UPI-app flow is opt-in (settings/global.payuUpiIntent); hosted page until it loads / by default.
  const [upiMode, setUpiMode] = useState<UpiIntentMode>("off");
  const [directUpi, setDirectUpi] = useState<DirectUpiSettings | null>(null);
  const [showDirect, setShowDirect] = useState(false);
  useEffect(() => {
    let alive = true;
    getDoc(doc(db, "settings", "global"))
      .then((snap) => {
        if (!alive) return;
        const g = snap.data() || {};
        setDirectUpi(directUpiFromSettings(g.directUpi));
        if (platform === "desktop") return;
        // Allowlisted testers (payuUpiIntentAllowlist, server-enforced too) get the S2S intent as default.
        const uid = auth.currentUser?.uid;
        const allow = Array.isArray(g.payuUpiIntentAllowlist) && !!uid && g.payuUpiIntentAllowlist.includes(uid);
        setUpiMode(allow ? "default" : parseUpiIntentMode(g.payuUpiIntent));
      })
      .catch(() => { /* keep hosted */ });
    return () => { alive = false; };
  }, [platform]);
  const primaryFlow = preferredPayFlow(platform, upiMode);
  const [otherLoading, setOtherLoading] = useState(false);
  const finishedRef = useRef(false);

  useEffect(() => {
    if (payUParams && formRef.current) {
      formRef.current.submit();
    }
  }, [payUParams]);

  const primaryLink = (l: UpiIntentLinks): string | null => {
    if (platform === "android" && isSafeUpiLink(l.androidIntent)) return l.androidIntent;
    return isSafeUpiLink(l.upi) ? l.upi : null;
  };

  const openLink = (link: string | null | undefined) => {
    if (!isSafeUpiLink(link)) return toast.error("Couldn't open the UPI app. Use Other payment methods.");
    window.location.href = link;
  };

  const startPayment = async (flow: PayFlow, val: number) => {
    const data: any = await call("generatePayURequest", {
      amount: val,
      purpose,
      returnTo: returnTo || (purpose === "order_payment" ? "checkout" : "profile"),
      checkoutKey: checkoutKey || "",
      flow,
      platform,
      ...(needPhone ? { phone: phone.replace(/\D/g, "").slice(-10) } : {}),
      ...(needEmail ? { email: email.trim() } : {}),
    });
    const txnid = data?.txnid || data?.params?.txnid;
    onBeforeRedirect?.(txnid, val);
    if (data?.flow === "upi_intent" && data?.upi) {
      finishedRef.current = false;
      savePendingUpi({ txnid, path: payuReturnPath(returnTo, purpose), at: Date.now() });
      setUpi({ txnid, amount: String(data.amount || val), links: data.upi, status: "waiting", startedAt: Date.now() });
      setLoading(false);
      // Android (Chrome / TWA): still inside the tap's user activation -> open the app chooser now.
      const link = primaryLink(data.upi);
      const act: any = (navigator as any).userActivation;
      if (platform === "android" && link && (!act || act.isActive)) {
        try { window.location.href = link; } catch { /* button below still works */ }
      }
      return;
    }
    if (data?.fallbackFrom === "upi_intent") {
      console.warn("UPI intent unavailable, using PayU hosted page:", data?.fallbackReason);
    }
    setPayUParams(data);
  };

  const amountValue = () => Math.round(parseFloat(amount) * 100) / 100;

  const handleInitiatePayment = async (flowOverride?: PayFlow) => {
    const val = amountValue();
    if (!val || val < 1) return toast.error("Enter valid amount (Min ₹1)");
    if (needPhone && !/^[6-9]\d{9}$/.test(phone.replace(/\D/g, "").slice(-10))) {
      return toast.error("Enter a valid 10-digit mobile number");
    }
    if (needEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim())) {
      return toast.error("Enter a valid email address");
    }

    setLoading(true);
    try {
      await startPayment(flowOverride || primaryFlow, val);
    } catch (error: any) {
      console.error(error);
      const msg = callableMessage(error, "Failed to initiate payment");
      if (msg.includes("PHONE_REQUIRED")) setNeedPhone(true);
      if (msg.includes("EMAIL_REQUIRED")) setNeedEmail(true);
      toast.error(msg.replace(/^(PHONE|EMAIL)_REQUIRED:\s*/, ""));
      setLoading(false);
    }
  };

  /** Mobile: full PayU hosted page (cards / netbanking / QR / wallets) with a fresh txnid. */
  const openOtherMethods = async () => {
    setOtherLoading(true);
    try {
      finishedRef.current = true; // stop reacting to the abandoned UPI intent
      clearPendingUpi();
      await startPayment("hosted", amountValue());
    } catch (error: any) {
      finishedRef.current = false;
      toast.error(callableMessage(error, "Failed to open PayU"));
      setOtherLoading(false);
    }
  };

  const onCredited = useCallback((txnid: string) => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    clearPendingUpi(txnid);
    // Same landing URL as PayU's surl redirect, so checkout auto-places the order exactly as before.
    navigate(`${payuReturnPath(returnTo, purpose)}?payu=success&txnid=${encodeURIComponent(txnid)}`);
    onClose();
  }, [navigate, returnTo, purpose, onClose]);
  const onCreditedRef = useRef(onCredited);
  onCreditedRef.current = onCredited;

  // While the customer is in their UPI app: live intent doc + throttled verify_payment polls.
  const upiTxnid = upi?.txnid;
  const upiWaiting = upi?.status === "waiting";
  useEffect(() => {
    if (!upiTxnid || !upiWaiting) return;
    let stopped = false;
    const startedAt = Date.now();
    upiModalWatching.txnid = upiTxnid;
    const handle = (status: string | undefined) => {
      if (stopped || !status) return;
      if (status === "credited") onCreditedRef.current(upiTxnid);
      else if (["failed", "expired", "mismatch"].includes(status)) {
        clearPendingUpi(upiTxnid);
        setUpi((u) => (u && u.txnid === upiTxnid ? { ...u, status: "failed" } : u));
      }
    };
    const unsub = onSnapshot(
      doc(db, "payu_intents", upiTxnid),
      (snap) => handle(snap.data()?.status),
      (e) => console.warn("payu intent listener", e)
    );
    let inFlight = false;
    const poll = async () => {
      if (stopped || inFlight || document.visibilityState !== "visible") return;
      if (Date.now() - startedAt > UPI_POLL_WINDOW_MS) {
        setUpi((u) => (u && u.txnid === upiTxnid && u.status === "waiting" ? { ...u, status: "timeout" } : u));
        return;
      }
      inFlight = true;
      try {
        const r: any = await call("payuIntentStatus", { txnid: upiTxnid });
        handle(r?.status);
      } catch (e) {
        console.warn("payuIntentStatus", e);
      } finally {
        inFlight = false;
      }
    };
    const timer = window.setInterval(poll, UPI_POLL_EVERY_MS);
    const onVis = () => { if (document.visibilityState === "visible") poll(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);
    return () => {
      stopped = true;
      if (upiModalWatching.txnid === upiTxnid) upiModalWatching.txnid = null;
      unsub();
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onVis);
    };
  }, [upiTxnid, upiWaiting]);

  const retryUpi = () => {
    setUpi(null);
    handleInitiatePayment("upi_intent");
  };

  const heading =
    title ||
    (purpose === "order_payment" ? "Pay online (PayU)" : "Add Money");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in">
      <div className="bg-white rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl">
        <div className="bg-gray-50 p-4 border-b border-gray-100 flex justify-between items-center">
          <h3 className="font-bold text-gray-800 flex items-center gap-2">
            <CreditCard className="text-indigo-600" size={20} /> {heading}
          </h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-200 rounded-full text-gray-500"
            type="button"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-6">
          {showDirect && directUpi ? (
            <DirectUpiPanel
              amount={amountValue()}
              isDesktop={platform === "desktop"}
              onBack={() => setShowDirect(false)}
              onDone={onClose}
            />
          ) : upi ? (
            <UpiIntentPanel
              upi={upi}
              platform={platform}
              primary={primaryLink(upi.links)}
              onOpen={openLink}
              onRetry={retryUpi}
              onOther={openOtherMethods}
              otherLoading={otherLoading}
            />
          ) : (
          <>
          {purpose === "order_payment" && (
            <p className="text-xs text-gray-500 text-center mb-4">
              PayU adds this amount to your wallet; your wallet then pays for the whole
              order, which is placed automatically when you return.
            </p>
          )}

          <div className="text-center mb-6">
            <div className="relative inline-block">
              <span className="absolute left-0 top-1/2 -translate-y-1/2 text-2xl font-bold text-gray-400">
                ₹
              </span>
              <input
                type="number"
                value={amount}
                onChange={(e) => !lockedAmount && setAmount(e.target.value)}
                readOnly={lockedAmount}
                placeholder="0"
                className="text-4xl font-bold text-gray-900 w-40 text-center outline-none border-b-2 border-indigo-100 focus:border-indigo-600 py-2"
                autoFocus={!lockedAmount}
              />
            </div>
          </div>

          {!lockedAmount && (
            <div className="grid grid-cols-4 gap-2 mb-6">
              {PRESET_AMOUNTS.map((amt) => (
                <button
                  key={amt}
                  type="button"
                  onClick={() => setAmount(amt.toString())}
                  className="py-2 rounded-lg border border-gray-200 text-sm font-semibold hover:border-indigo-500 hover:text-indigo-600 bg-gray-50"
                >
                  ₹{amt}
                </button>
              ))}
            </div>
          )}

          {(needPhone || needEmail) && (
            <div className="space-y-3 mb-6">
              <p className="text-xs text-gray-600">PayU needs your contact details for the receipt.</p>
              {needPhone && (
                <div className="flex items-center border rounded-md">
                  <span className="bg-gray-100 px-3 py-2 text-gray-700 border-r">+91</span>
                  <input
                    type="tel"
                    inputMode="numeric"
                    maxLength={10}
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                    placeholder="10-digit mobile"
                    className="flex-1 px-3 py-2 outline-none"
                  />
                </div>
              )}
              {needEmail && (
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full border rounded-md px-3 py-2 outline-none"
                />
              )}
            </div>
          )}

          <Button
            onClick={() => handleInitiatePayment()}
            isLoading={loading}
            disabled={!amount}
            className="w-full py-3 text-lg bg-indigo-600 hover:bg-indigo-700 shadow-lg"
          >
            {loading ? "Please wait..." : primaryFlow === "hosted" ? "Pay Securely" : "Pay with UPI app"}
          </Button>

          {primaryFlow === "hosted" && (
            <p className="text-[11px] text-gray-500 text-center mt-2">
              Opens PayU: scan the UPI QR with any UPI app, or pay by card or netbanking.
            </p>
          )}

          {primaryFlow === "hosted_upi" && (
            <p className="text-[11px] text-gray-500 text-center mt-2">
              GPay, PhonePe, Paytm, BHIM, CRED… — tap your app on the next screen and approve.
            </p>
          )}

          {primaryFlow !== "hosted" && platform !== "desktop" && (
            <button
              type="button"
              onClick={() => handleInitiatePayment("hosted")}
              disabled={!amount || loading}
              className="w-full mt-3 text-sm text-indigo-700 underline disabled:opacity-50"
            >
              Card / netbanking / other options
            </button>
          )}

          {primaryFlow !== "upi_intent" && showDirectUpiOption(platform, upiMode) && (
            <button
              type="button"
              onClick={() => handleInitiatePayment("upi_intent")}
              disabled={!amount || loading}
              className="w-full mt-3 text-xs text-gray-600 underline disabled:opacity-50"
            >
              Pay directly in UPI app (beta)
            </button>
          )}
          {directUpi && purpose === "wallet_recharge" && !lockedAmount && (
            <button
              type="button"
              onClick={() => {
                const v = amountValue();
                if (!v || v < 1 || v > 50000) return toast.error("Enter valid amount (₹1 – ₹50000)");
                setShowDirect(true);
              }}
              disabled={!amount || loading}
              className="w-full mt-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 disabled:opacity-50"
            >
              PayU not working? Pay directly by UPI
            </button>
          )}
          </>
          )}

          <div className="mt-4 flex items-center justify-center gap-2 text-[10px] text-gray-400">
            <ShieldCheck size={12} /> Secured by PayU
          </div>

          {payUParams && (
            <form
              action={payUParams.action}
              method="post"
              ref={formRef}
              style={{ display: "none" }}
            >
              {POST_FIELDS.filter((k) => payUParams.params?.[k] !== undefined).map((k) => (
                <input key={k} type="hidden" name={k} value={payUParams.params?.[k] ?? ""} />
              ))}
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

const UpiIntentPanel: React.FC<{
  upi: UpiState;
  platform: string;
  primary: string | null;
  onOpen: (link: string | null | undefined) => void;
  onRetry: () => void;
  onOther: () => void;
  otherLoading: boolean;
}> = ({ upi, platform, primary, onOpen, onRetry, onOther, otherLoading }) => {
  const amountLabel = `₹${Number(upi.amount).toFixed(2)}`;
  return (
    <div className="text-center">
      <div className="text-3xl font-bold text-gray-900 mb-1">{amountLabel}</div>
      {upi.links.merchantName && (
        <div className="text-xs text-gray-500 mb-4">to {upi.links.merchantName}</div>
      )}

      {upi.status === "failed" ? (
        <div className="mb-4 rounded-lg bg-red-50 text-red-700 text-sm p-3">
          Payment failed or was cancelled in the UPI app. No money was added.
        </div>
      ) : upi.status === "timeout" ? (
        <div className="mb-4 rounded-lg bg-amber-50 text-amber-800 text-sm p-3">
          Still waiting for confirmation. If you paid, your wallet is credited automatically (usually within 15 minutes).
        </div>
      ) : (
        <div className="mb-4 flex items-center justify-center gap-2 text-sm text-gray-600">
          <Loader2 size={16} className="animate-spin" /> Waiting for payment confirmation…
        </div>
      )}

      {upi.status === "failed" ? (
        <Button onClick={onRetry} className="w-full py-3 text-lg bg-indigo-600 hover:bg-indigo-700">
          Try UPI again
        </Button>
      ) : platform === "ios" ? (
        <div className="grid grid-cols-2 gap-2">
          {IOS_UPI_APPS.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => onOpen(upi.links.ios?.[a.id])}
              className="py-3 rounded-lg border border-gray-200 text-sm font-semibold hover:border-indigo-500 bg-gray-50"
            >
              {a.label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => onOpen(upi.links.upi)}
            className="py-3 rounded-lg border border-gray-200 text-sm font-semibold hover:border-indigo-500 bg-gray-50"
          >
            Other UPI app
          </button>
        </div>
      ) : (
        <Button
          onClick={() => onOpen(primary)}
          className="w-full py-3 text-lg bg-indigo-600 hover:bg-indigo-700 shadow-lg"
        >
          <Smartphone size={18} className="mr-2" /> Open UPI app
        </Button>
      )}

      {upi.status === "waiting" && (
        <p className="text-[11px] text-gray-500 mt-3">
          {platform === "ios"
            ? "Pick your UPI app, approve the payment, then come back here."
            : "Choose your UPI app (GPay, PhonePe, Paytm…), approve, then come back here."}
        </p>
      )}

      <button
        type="button"
        onClick={onOther}
        disabled={otherLoading}
        className="w-full mt-4 py-2 rounded-lg border border-indigo-300 text-sm font-semibold text-indigo-700 hover:bg-indigo-50 disabled:opacity-50"
      >
        {otherLoading ? "Opening PayU…" : "Didn't work? Pay on the PayU page (UPI apps, QR, cards)"}
      </button>
    </div>
  );
};
